import { useMemo, useState, useCallback } from 'react'
import {
  View, Text, TextInput, TouchableOpacity, SectionList,
  Modal, StyleSheet
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import { fonts, radius } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { hapticLight } from '../../lib/haptics'
import Ionicons from '@expo/vector-icons/Ionicons'
import { InsightCell, BibTag } from '../../components/Pick/ui'
import RiderInsightSheet from '../../components/Pick/RiderInsightSheet'
import RiderFace, { TeamJersey } from '../../components/RiderFace'
import { NAT_TEAM_CODE } from '../../lib/nationKits'

// ─── Estrae nome squadra dal rider_name "[Team] SURNAME Name" ────────
function extractTeamName(riderName) {
  const m = riderName?.match(/^\[(.+?)\]/)
  return m ? m[1] : '—'
}

// ─── Bandiera emoji da codice ISO a 2 lettere (dim_rider.id_national) ─
// Caso speciale 'RF' = corridori "Refugee" (nazionalità PCS "REF", non
// una vera nazione ISO): niente combinazione di lettere regionali senza
// senso, mostriamo una bandiera bianca neutra.
function flagEmoji(isoCode) {
  const cc = isoCode?.trim().toUpperCase()
  if (cc === 'RF') return '🏳️'
  if (!cc || cc.length !== 2) return '🏳️'
  return String.fromCodePoint(...[...cc].map(ch => 127397 + ch.charCodeAt(0)))
}

// ─── Nome nazionalità leggibile dal codice ISO ────────────────────────
// Legge il nome esteso da dim_national.name (passato come mappa
// {id_national: name} da InserimentoPick) invece di affidarsi a
// Intl.DisplayNames, che sul dispositivo può non essere disponibile e
// mostrare il solo codice ("AU", "BE"...). Il nome in tabella è già in
// inglese (compreso 'RF' → 'Refugee' una volta applicata la fix SQL).
function nationalityLabel(isoCode, namesMap) {
  const cc = isoCode?.trim().toUpperCase()
  if (!cc) return '—'
  return namesMap?.[cc] ?? (cc === 'RF' ? 'Refugee' : cc)
}

// ─── Raggruppa allRiders in sezioni: per squadra, o per nazionalità ───
// nelle gare di nazionali (isNationalRace) — id_team vale sempre 'NAT'
// in quel caso, quindi si raggruppa per id_national mostrando la bandiera.
// Gara normale: i corridori della squadra generica NAT ("locali" senza codice squadra) stanno in un gruppo per paese, con la maglia del paese.
const isNatRider = (r) => r.uci_code === NAT_TEAM_CODE
const groupKeyOfRider = (r, isNationalRace) => isNationalRace ? (r.id_national ?? '—') : (isNatRider(r) ? `NAT-${r.id_national ?? '—'}` : r.id_team)

function buildSections(riders, isNationalRace, namesMap) {
  const map = new Map()

  const groupKey   = (r) => groupKeyOfRider(r, isNationalRace)
  const groupLabel = (r) => (isNationalRace || isNatRider(r)) ? nationalityLabel(r.id_national, namesMap) : extractTeamName(r.label_raw)

  // Gara normale: ordina per bib (le squadre escono quindi nell'ordine
  // del loro bib più basso, come nella startlist ufficiale).
  // Gara di nazionali: non c'è un bib "di squadra" univoco tra nazioni,
  // quindi si ordina per nome nazionalità e poi per cognome.
  const sorted = [...riders].sort((a, b) => {
    if (isNationalRace) {
      const la = groupLabel(a), lb = groupLabel(b)
      if (la !== lb) return la.localeCompare(lb)
      return (a.surname ?? '').localeCompare(b.surname ?? '')
    }
    if (a.bib !== b.bib) return (a.bib ?? 999) - (b.bib ?? 999)
    return groupLabel(a).localeCompare(groupLabel(b))
  })

  for (const r of sorted) {
    const key = groupKey(r)
    if (!map.has(key)) {
      map.set(key, {
        id_team: key,
        uci_code: isNationalRace ? r.id_national : r.uci_code,   // nei gruppi NAT resta 'NAT'
        team_name: groupLabel(r),
        kit: isNationalRace ? null : (r.kit ?? null),
        isNational: isNationalRace,
        data: [],
      })
    }
    map.get(key).data.push(r)
  }

  // Map mantiene l'ordine di inserimento → gruppi già ordinati sopra
  return Array.from(map.values())
}

export default function ModaleSelezione({
  allRiders,
  selected,
  limit,
  onAdd,
  onRemove,
  onClose,
  usedRiderIds = null,
  usedStage = {},   // id_rider -> numero tappa in cui e' stato usato (per il badge "T7")
  isNationalRace = false,
  nationalNames = {},
  insights = {},
}) {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { t } = useTranslation()
  const insets = useSafeAreaInsets()
  const [search, setSearch] = useState('')
  const [infoRider, setInfoRider] = useState(null)   // scheda insight aperta da questa finestra (dentro, perche' un Modal non si apre sopra un altro)
  const groupKeyOf = useCallback(r => groupKeyOfRider(r, isNationalRace), [isNationalRace])
  const [openTeams, setOpenTeams] = useState(() => new Set(allRiders.map(groupKeyOf)))
  const isComplete = selected.length === limit

  const toggleTeam = useCallback((id_team) => {
    hapticLight()
    setOpenTeams(prev => {
      const next = new Set(prev)
      if (next.has(id_team)) next.delete(id_team)
      else next.add(id_team)
      return next
    })
  }, [])

  // ── Filtra + raggruppa ────────────────────────────────────────────
  const sections = useMemo(() => {
    const q = search.trim().toLowerCase()
    let riders = allRiders
    if (q) {
      riders = allRiders.filter(r => {
        const nameMatch = r.label?.toLowerCase().includes(q)
        const groupMatch = (isNationalRace || isNatRider(r) ? nationalityLabel(r.id_national, nationalNames) : extractTeamName(r.label_raw))?.toLowerCase().includes(q)
        const uciMatch = r.uci_code?.toLowerCase().includes(q)
        const bibMatch = /^\d+$/.test(q) && String(r.bib ?? '').startsWith(q)   // cerca per numero di gara
        return nameMatch || groupMatch || uciMatch || bibMatch
      })
    }
    return buildSections(riders, isNationalRace, nationalNames)
  }, [allRiders, search, isNationalRace, nationalNames])

  // Auto-espandi squadre con match durante ricerca
  useMemo(() => {
    if (search.trim()) {
      setOpenTeams(new Set(sections.map(s => s.id_team)))
    }
  }, [search]) // eslint-disable-line react-hooks/exhaustive-deps

  const renderSectionHeader = useCallback(({ section }) => {
    const isOpen = openTeams.has(section.id_team)
    const teamSelected = section.data.filter(r => selected.find(s => s.value === r.value)).length
    return (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={() => toggleTeam(section.id_team)}
        style={[styles.teamHeader, { borderBottomColor: tc.border }]}
      >
        {/* Jersey (gara normale) oppure Bandiera (gara di nazionali) */}
        <View style={styles.jerseyWrap}>
          {section.isNational ? (
            <Text style={{ fontSize: 20 }}>{flagEmoji(section.uci_code)}</Text>
          ) : (
            <TeamJersey kit={section.kit} size={56} />
          )}
        </View>

        {/* Nome squadra + count */}
        <View style={{ flex: 1 }}>
          <Text style={[styles.teamName, { color: tc.text }]} numberOfLines={1}>
            {section.team_name}
          </Text>
          <Text style={[styles.teamSub, { color: tc.textMuted }]}>
            {t('pick.riders_count', { count: section.data.length })}
            {teamSelected > 0 && (
              <Text style={{ color: tc.rosaText }}>{` · ${t('pick.riders_selected', { count: teamSelected })}`}</Text>
            )}
          </Text>
        </View>

        {/* Chevron */}
        <Ionicons name={isOpen ? 'chevron-up' : 'chevron-down'} size={12} color={tc.textMuted} />

      </TouchableOpacity>
    )
  }, [openTeams, selected, tc, styles, toggleTeam])

  const renderItem = useCallback(({ item: rider, section }) => {
    if (!openTeams.has(section.id_team)) return null

    const isSelected = !!selected.find(s => s.value === rider.value)
    const isUsed = !isSelected && usedRiderIds?.has(rider.value)
    const isFull = selected.length >= limit && limit > 1   // con un solo corridore, toccarne un altro lo sostituisce
    const disabled = !isSelected && (isFull || isUsed)
    const slotNum = isSelected ? selected.findIndex(s => s.value === rider.value) + 1 : null

    return (
      <View style={[styles.riderRowWrap, { borderBottomColor: tc.border }, isSelected && { backgroundColor: tc.rosaSubtle }]}>
      <TouchableOpacity
        style={[styles.riderRow, disabled && styles.riderRowDisabled]}
        onPress={() => {
          if (isSelected) {
            hapticLight()
            const idx = selected.findIndex(s => s.value === rider.value)
            onRemove(idx)
          } else if (!disabled) {
            hapticLight()
            onAdd(rider)
          }
        }}
        activeOpacity={disabled ? 1 : 0.7}
      >
        <View style={{ marginRight: 10 }}><RiderFace avatar={rider.avatar} kit={isNationalRace ? null : rider.kit} size={38} /></View>
        <Text
          style={[
            { fontSize: 13, flex: 1 },
            { color: isUsed ? tc.textMuted : tc.text },
            isUsed && { textDecorationLine: 'line-through' },
          ]}
          numberOfLines={1}
        >
          <Text style={{ fontFamily: fonts.condensedBold }}>{rider.surname} </Text>
          <Text style={{ fontFamily: fonts.condensedRegular }}>{rider.firstName}</Text>
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <BibTag bib={rider.bib} />
          {isUsed && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              {!!usedStage[rider.value] && <Text style={{ fontSize: 11, color: tc.textMuted, fontFamily: fonts.condensedBold }}>T{usedStage[rider.value]}</Text>}
              <Ionicons name="lock-closed-outline" size={13} color={tc.textMuted} />
            </View>
          )}
          {isSelected && (
            <Text style={[styles.riderCheck, { color: tc.rosaText }]}>
              {slotNum}° ✓
            </Text>
          )}
        </View>
      </TouchableOpacity>
      <InsightCell insight={insights[rider.value]} name={rider.label} onPress={() => { hapticLight(); setInfoRider(rider) }} />
      </View>
    )
  }, [openTeams, selected, limit, usedRiderIds, usedStage, tc, styles, onAdd, onRemove, insights])

  return (
    <Modal visible animationType="slide" onRequestClose={onClose}>
      <View style={[styles.container, { backgroundColor: tc.bg, paddingTop: insets.top }]}>

        {/* Header */}
        <View style={[styles.header, { borderBottomColor: tc.border }]}>
          <View style={{ flex: 1 }}>
            <Text style={[styles.title, { color: tc.text }]}>{t('pick.select_riders')}</Text>
          </View>
          <Text style={[styles.counter, isComplete && { color: '#10B981' }]}>
            {selected.length}/{limit}
          </Text>
          <TouchableOpacity
            style={[styles.confirmBtn, { backgroundColor: selected.length === 0 ? tc.bgCard2 : tc.rosa }]}
            onPress={() => { hapticLight(); onClose() }}
          >
            <Text style={[styles.confirmBtnText, { color: selected.length === 0 ? tc.textDim : tc.brandText }]}>
              {isComplete ? t('common.confirm') : `OK (${selected.length}/${limit})`}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.closeBtn, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}
            onPress={() => { hapticLight(); onClose() }}
          >
            <Ionicons name="close" size={16} color={tc.textMuted} />
          </TouchableOpacity>
        </View>

        {/* Selezione corrente */}
        {selected.length > 0 && (
          <View style={[styles.selectedList, { backgroundColor: tc.bgCard, borderBottomColor: tc.rosaBorder }]}>
            {selected.map((rider, i) => (
              <View
                key={rider.value}
                style={[
                  styles.selectedRow,
                  i < selected.length - 1 && { borderBottomWidth: 1, borderBottomColor: tc.border },
                ]}
              >
                <Text style={[styles.selectedSlot, { color: tc.rosaText }]}>{i + 1}°</Text>
                <Text style={[styles.selectedName, { color: tc.text }]} numberOfLines={1}>
                  {rider.label}
                </Text>
                <TouchableOpacity onPress={() => { hapticLight(); onRemove(i) }} style={[styles.removeBtn, { backgroundColor: tc.rosaSubtle }]}>
                  <Text style={[styles.removeBtnText, { color: tc.rosaText }]}>−</Text>
                </TouchableOpacity>
              </View>
            ))}
          </View>
        )}

        {/* Search */}
        <View style={[styles.searchRow, { borderBottomColor: tc.border, backgroundColor: tc.bg }]}>
          <Ionicons name="search-outline" size={16} color={tc.textMuted} />
          <TextInput
            style={[styles.searchInput, { color: tc.text }]}
            value={search}
            onChangeText={setSearch}
            placeholder={t('pick.search_rider')}
            placeholderTextColor={tc.textDim}
            autoCorrect={false}
            autoCapitalize="none"
          />
          {search ? (
            <TouchableOpacity onPress={() => setSearch('')}>
              <Ionicons name="close" size={16} color={tc.textMuted} />
            </TouchableOpacity>
          ) : null}
          <TouchableOpacity onPress={() => {
            if (openTeams.size > 0) setOpenTeams(new Set())
            else setOpenTeams(new Set(allRiders.map(groupKeyOf)))
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginLeft: 8 }}>
              <Ionicons name={openTeams.size > 0 ? 'chevron-up' : 'chevron-down'} size={14} color={tc.rosaText} />
              <Text style={{ color: tc.rosaText, fontSize: 16, fontFamily: fonts.condensed }}>
                {openTeams.size > 0 ? t('pick.collapse_all') : t('pick.expand_all')}
              </Text>
            </View>
          </TouchableOpacity>
        </View>

        {/* SectionList accordion */}
        <SectionList
          sections={sections}
          keyExtractor={item => String(item.value)}
          keyboardShouldPersistTaps="handled"
          stickySectionHeadersEnabled={false}
          contentContainerStyle={{ paddingBottom: insets.bottom + 16 }}
          renderSectionHeader={renderSectionHeader}
          renderItem={renderItem}
          ListEmptyComponent={
            <View style={{ padding: 24, alignItems: 'center' }}>
              <Text style={{ color: tc.textMuted, fontSize: 14 }}>
                {t('pick.no_rider_found')}
              </Text>
            </View>
          }
        />
        {infoRider && <RiderInsightSheet riders={[infoRider]} insights={insights} isNationalRace={isNationalRace} onClose={() => setInfoRider(null)} />}
      </View>
    </Modal>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container: { flex: 1 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 14, borderBottomWidth: 1 },
  title: { fontFamily: fonts.condensedBold, fontSize: 18 },
  counter: { fontFamily: fonts.condensedBold, fontSize: 14, color: tc.rosaText },
  confirmBtn: { borderRadius: radius.md, paddingHorizontal: 14, paddingVertical: 8 },
  confirmBtnText: { fontFamily: fonts.condensedBold, fontSize: 13 },
  closeBtn: { borderWidth: 1, borderRadius: radius.md, width: 32, height: 32, alignItems: 'center', justifyContent: 'center' },
  selectedList: { borderBottomWidth: 1 },
  selectedRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, paddingHorizontal: 16 },
  selectedSlot: { fontFamily: fonts.condensedBold, fontSize: 18, width: 28 },
  selectedName: { flex: 1, fontSize: 14, fontWeight: '500' },
  removeBtn: { borderRadius: 12, width: 24, height: 24, alignItems: 'center', justifyContent: 'center' },
  removeBtnText: { fontSize: 16, lineHeight: 18 },
  searchRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, paddingHorizontal: 16, borderBottomWidth: 1 },
  searchInput: { flex: 1, fontSize: 15, backgroundColor: 'transparent' },
  // Team header
  teamHeader: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 6, paddingHorizontal: 16, borderBottomWidth: 1, backgroundColor: tc.bgCard2, borderLeftWidth: 2, borderLeftColor: tc.rosa },
  jerseyWrap: { width: 56, minHeight: 24, alignItems: 'center', justifyContent: 'center' },
  teamName: { fontFamily: fonts.condensedBold, fontSize: 16, textTransform: 'uppercase' },
  teamSub: { fontSize: 12, marginTop: 1 },
  // Rider row
  riderRowWrap: { flexDirection: 'row', alignItems: 'stretch', borderBottomWidth: 1 },
  riderRow: { flex: 1, minWidth: 0, minHeight: 52, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 12, paddingLeft: 24, paddingRight: 12 }, riderRowDisabled: { opacity: 0.35 },
  riderCheck: { fontSize: 13, fontFamily: fonts.condensed },
})